import axios from 'axios';
import { ApiException } from '../../utils/exceptions';
import { auth, remoteConfig } from '../../config/firebase-config';
import { getString } from 'firebase/remote-config';
import LocalAppSettings from '../../utils/local_app_settings';
import { DEFAULTS } from '../dynamic_config/dynamic_config_service';

export function getApiBaseUrl(dynamicState = {}) {
  if (process.env.REACT_APP_API_BASE_URL) return process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '');
  const devMode = localStorage.getItem('dev_mode') === 'true';
  const key = devMode ? 'dev_api_url' : 'prod_api_url';
  const configured = devMode ? dynamicState.developmentApiServerBaseUrl : dynamicState.prodApiServerBaseUrl;
  return (configured || (remoteConfig && getString(remoteConfig, key)) || DEFAULTS[key]).replace(/\/$/, '');
}

const ApiService = {
  reformatData(contentType, data) {
    return contentType === 'application/json' ? JSON.stringify(data) : data;
  },
    
  async request(method, path, headers = {}, data = {}, options = {}) {
    try {
      // A request owns its URL and headers; public calls and uploads must not
      // inherit authentication or content types from an earlier request.
      const baseURL = options.dynamicState?.prodApiServerBaseUrl === 'https://maps.googleapis.com'
        ? 'https://maps.googleapis.com' : getApiBaseUrl(options.dynamicState);
      const requestHeaders = {};
      const isFormData = data instanceof FormData;
      if (!isFormData) requestHeaders['Content-Type'] = headers.contentType || 'application/json';
      if ('authorization' in headers && auth.currentUser) {
        requestHeaders.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
      }
      const config = { method, url: path, baseURL, headers: requestHeaders, timeout: 60000 };
      if (method === 'get') {
        // GET data is Axios configuration, never a JSON body.
        config.params = { ...(data?.params || {}) };
        config.cancelToken = data?.cancelToken;
        config.signal = data?.signal;
        config.responseType = data?.responseType;
      } else {
        config.data = options.reformatData && !isFormData
          ? this.reformatData(headers.contentType, data) : data;
      }
      if (baseURL !== 'https://maps.googleapis.com') {
        config.params = { lang: LocalAppSettings.loadSettings().locale, ...config.params };
      }
      return await axios.request(config);
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      const body = error.response?.data;
      throw new ApiException({
        name: `${method.toUpperCase()} Request Error`,
        message: body?.friendlyMessage || body?.error?.message || body?.message || error.message || 'Unable to reach the server',
        errorCode: error.response?.status ?? null,
      });
    }
  },
  get(path, headers, data, options) { return this.request('get', path, headers, data, options); },
  post(path, headers, data, options) { return this.request('post', path, headers, data, options); },
  put(path, headers, data, options) { return this.request('put', path, headers, data, options); },
  patch(path, headers, data, options) { return this.request('patch', path, headers, data, options); },
  delete(path, headers, data, options) { return this.request('delete', path, headers, data, options); },
};

export default ApiService;
